import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import classNames from "classnames";
import styles from "./ChooseVendingMachineModal.module.scss";
import Loader from "../../loader/Loader";
import vendingControllersService from "../../../apis/services/admin/vendingControllersService";

/**
 * Post-attach Intervend step: the controller now needs a vending machine on IV. Offers the
 * operator IV machines WITHOUT a controller (under the same company) to link to, or creates a
 * new machine (name prefilled from the controller, editable). If IV is unreachable the operator
 * can still push the plain attach (legacy behavior — controller left without a machine).
 *
 * onConfirm(vendingMachine) — vendingMachine is
 *   { action: 'attach', vendingMachineId } | { action: 'create', name } | null (plain sync).
 */
const ChooseVendingMachineModal = ({ modalOpen, closeModal, vendingControllerId, onConfirm, loading }) => {
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  // true when the sync will be a DETACH (no fiscal device) — no VM step then.
  const [detachOnly, setDetachOnly] = useState(false);
  // true on the Simons prep taxpayer — mirror the VC<->FD association but defer the machine (plain confirm).
  const [deferVm, setDeferVm] = useState(false);
  const [vms, setVms] = useState([]);
  const [selectedVmId, setSelectedVmId] = useState(null);
  const [newVmName, setNewVmName] = useState("");

  useEffect(() => {
    if (!modalOpen || !vendingControllerId) return;
    let cancelled = false;
    setOptionsLoading(true);
    setOptionsError(null);
    setDetachOnly(false);
    setDeferVm(false);
    setVms([]);
    setSelectedVmId(null);

    vendingControllersService
      .getIntervendVmOptions({ id: vendingControllerId })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        setDetachOnly(!!data.excluded);
        setDeferVm(!!data.deferVm);
        setVms(data.vms || []);
        setNewVmName(data.suggestedName || "");
      })
      .catch((err) => {
        if (cancelled) return;
        setOptionsError(err?.message || "Intervend trenutno nije dostupan");
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [modalOpen, vendingControllerId]);

  const confirmAttach = () => {
    if (!selectedVmId || loading) return;
    onConfirm({ action: "attach", vendingMachineId: selectedVmId });
  };

  const confirmCreate = () => {
    if (!newVmName.trim() || loading) return;
    onConfirm({ action: "create", name: newVmName.trim() });
  };

  const hasVms = vms.length > 0;

  return (
    <Modal open={modalOpen} onClose={loading ? undefined : closeModal}>
      <div className={styles.modalWrap}>
        <div className={styles.modalInner}>
          <p className={styles.title}>Sinhronizovati povezivanje sa Intervend-om?</p>
          <p className={styles.description}>
            {deferVm
              ? "Povezivanje kontrolera sa fiskalnim uređajem biće preslikano na Intervend. Vending mašina se za sada ne kreira — napraviće se kada se LPFR dodeli firmi klijenta."
              : "Kontroleru je potrebna vending mašina na Intervend-u. Poveži ga na postojeću mašinu bez kontrolera ili kreiraj novu. Fiskalni zapis je već sačuvan; ovo je opcionalno."}
          </p>

          {optionsLoading ? (
            <div className={styles.loaderWrap}>
              <Loader size="2.5em" />
            </div>
          ) : detachOnly ? (
            <>
              <p className={styles.hint}>
                Kontroler nema fiskalni uređaj (ili je na test firmi) — na Intervend-u će biti otkačen.
              </p>
              <div className={styles.buttonsWrapper}>
                <button
                  className={styles.primaryBtn}
                  onClick={() => onConfirm(null)}
                  disabled={loading}
                >
                  {loading ? "Šaljem..." : "Sinhronizuj"}
                </button>
                <p
                  className={styles.cancel}
                  onClick={() => {
                    if (!loading) closeModal();
                  }}
                >
                  Ne, hvala
                </p>
              </div>
            </>
          ) : deferVm ? (
            <>
              <p className={styles.hint}>
                Kontroler će biti povezan sa fiskalnim uređajem na Intervend-u. Kada se karticom LPFR
                dodeli firmi klijenta na terenu, isto povezivanje se automatski primenjuje i na
                Intervend-u (i tada se kreira vending mašina).
              </p>
              <div className={styles.buttonsWrapper}>
                <button
                  className={styles.primaryBtn}
                  onClick={() => onConfirm(null)}
                  disabled={loading}
                >
                  {loading ? "Šaljem..." : "Poveži na Intervend-u"}
                </button>
                <p
                  className={styles.cancel}
                  onClick={() => {
                    if (!loading) closeModal();
                  }}
                >
                  Ne, hvala
                </p>
              </div>
            </>
          ) : optionsError ? (
            <>
              <p className={styles.errorText}>{optionsError}</p>
              <div className={styles.buttonsWrapper}>
                <button
                  className={styles.primaryBtn}
                  onClick={() => onConfirm(null)}
                  disabled={loading}
                >
                  {loading ? "Šaljem..." : "Sinhronizuj bez mašine"}
                </button>
                <p
                  className={styles.cancel}
                  onClick={() => {
                    if (!loading) closeModal();
                  }}
                >
                  Ne, hvala
                </p>
              </div>
            </>
          ) : (
            <>
              {hasVms && (
                <>
                  <p className={styles.sectionLabel}>Postojeće mašine bez kontrolera</p>
                  <div className={styles.vmList}>
                    {vms.map((vm) => (
                      <div
                        key={vm.id}
                        className={classNames(
                          styles.vmRow,
                          selectedVmId === vm.id && styles.vmRowSelected
                        )}
                        onClick={() => setSelectedVmId(selectedVmId === vm.id ? null : vm.id)}
                      >
                        <span
                          className={classNames(
                            styles.vmRadio,
                            selectedVmId === vm.id && styles.vmRadioSelected
                          )}
                        />
                        <div className={styles.vmText}>
                          <p className={styles.vmName}>{vm.name || `Mašina #${vm.id}`}</p>
                          {vm.businessUnit?.name ? (
                            <p className={styles.vmMeta}>{vm.businessUnit.name}</p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.buttonsWrapper}>
                    <button
                      className={styles.primaryBtn}
                      onClick={confirmAttach}
                      disabled={!selectedVmId || loading}
                    >
                      {loading ? "Šaljem..." : "Poveži na izabranu mašinu"}
                    </button>
                  </div>
                  <div className={styles.divider}>ili kreiraj novu</div>
                </>
              )}

              {!hasVms && <p className={styles.sectionLabel}>Nova vending mašina</p>}
              <input
                className={styles.nameInput}
                value={newVmName}
                onChange={(e) => setNewVmName(e.target.value)}
                placeholder="Naziv mašine"
                disabled={loading}
              />
              <div className={styles.buttonsWrapper}>
                <button
                  className={styles.primaryBtn}
                  onClick={confirmCreate}
                  disabled={!newVmName.trim() || loading}
                >
                  {loading ? "Šaljem..." : "Kreiraj novu mašinu"}
                </button>
                <p
                  className={styles.cancel}
                  onClick={() => {
                    if (!loading) closeModal();
                  }}
                >
                  Ne, hvala
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ChooseVendingMachineModal;
